import Component from './Component';
import GeometryAlgorithm from '../geometry/GeometryAlgorithm';
import VectorTileLayer from '../layer/VectorTileLayer';
import VectorLayer from '../layer/VectorLayer';
import PartitionLayer from '../layer/PartitionLayer';
import SymbolLayer from '../layer/SymbolLayer';
import Util from '../utils/util';

/**
 * 要素选择器类.
 */
export default class FeatureSelector extends Component {
    /**
     * 构造方法.
     * @returns {undefined}
     */
    constructor(options) {
        super(options);

        this.geometryAlgorithm = GeometryAlgorithm.getInstance();
    }

    onAdd(map) {
        super.onAdd(map);

        if (map.GeojsonConverter) {
            this._geojsonConverter = map.GeojsonConverter;
        }
    }

    onRemove(map) {
        this._geojsonConverter = null;

        super.onRemove(map);
    }

    /**
     * 根据一个几何范围和属性信息选择要素
     * @param {object}   geometry 经纬度坐标，支持所有geoJson几何类型
     * @param {function} filter 过滤函数
     * @returns {array} 所有被选中的feature数组
     */
    selectByGeometry(geometry, filter) {
        // 用于选择的几何转换成屏幕内像素坐标
        const pixelGeo = this._geojsonConverter.lngLatToPixel(geometry);

        // 计算几何所跨越的瓦片
        const tileIds = this._getTilesFromContainerGeo(pixelGeo);
        const features = this._getFeaturesFromLayers(tileIds);

        const selectedFeatures = [];
        for (const feature of features) {
            if (this._isHitGeometry(geometry, feature.geometry)) {
                if (Util.isFunction(filter)) {
                    if (filter(feature)) {
                        selectedFeatures.push(feature);
                    }
                } else {
                    selectedFeatures.push(feature);
                }
            }
        }

        return selectedFeatures;
    }

    /**
     * 根据属性信息选择要素
     * @param {object}   properties 属性信息
     * @param {function} filter 过滤函数
     * @returns {array} 所有被选中的feature数组
     */
    selectByProperties(properties, filter) {
        const features = this._getFeaturesFromLayers();

        const selectedFeatures = [];
        for (const feature of features) {
            if (this._isHitProperties(feature, properties)) {
                if (Util.isFunction(filter)) {
                    if (filter(feature)) {
                        selectedFeatures.push(feature);
                    }
                } else {
                    selectedFeatures.push(feature);
                }
            }
        }

        return selectedFeatures;
    }

    /**
     * 根据fid选择要素
     * @param {string}  fid fid
     * @returns {object} 被选中的feature对象
     */
    selectByFid(fid) {
        const features = this._getFeaturesFromLayers();

        return features.find(feature => feature.fid === fid);
    }
    /**
     * 根据要素类型和要素id选择要素
     * 如果未找到匹配feature返回null
     * @param {Number} id - 要素的唯一标识
     * @param {boolean} isRepeat - 是否返回重复的瓦片数据
     * @returns {null|Object} 被选中的要素
     */
    selectByFeatureId(id, isRepeat = false) {
        if (!id) {
            return null;
        }

        const features = this._getFeaturesFromLayers();
        if (isRepeat) {
            return features.filter(it => it.id === id) || null;
        }
        return features.find(it => it.id === id) || null;
    }
    /**
     * 根据传入的过滤函数选择要素
     * 注意此接口效率较低,切勿在循环中调用
     * @param {function} func - 自定义函数，接受feature参数，返回bool值
     * @returns {array} 所有被选中的feature数组
     */
    selectByFunction(func) {
        const features = this._getFeaturesFromLayers();

        return features.filter(feature => func(feature));
    }
    
    /**
     * 根据传入的过滤函数选择要素
     * 注意此接口效率较低,切勿在循环中调用
     * @param {array} tileIds 瓦片Id数组
     * @returns {array} 所有被选中的feature数组
     */
    _getFeaturesFromLayers(tileIds) {
        const features = [];

        this._map.eachLayer(layer => {
            if (layer instanceof VectorTileLayer && layer.selectable()) {
                for (const tile of Object.values(layer.tiles)) {
                    if (tileIds && tileIds.length > 0) {
                        if (tileIds.indexOf(tile.name) >= 0 && tile.data) {
                            features.push(...tile.data);
                        }
                    } else {
                        if (tile.data) {
                            features.push(...tile.data);
                        }
                    }
                }
            } else if (layer instanceof VectorLayer && layer.selectable()) {
                features.push(...layer.data);
            } else if (layer instanceof PartitionLayer && layer.selectable()) {
                for (const tile of Object.values(layer.data)) {
                    features.push(...tile);
                }
            }
        });

        // 去重
        const exists = {};
        return features.reduce((arr, cur) => {
            if (!exists[cur.fid]) {
                arr.push(cur);
                exists[cur.fid] = true;
            }

            return arr;
        }, []);
    }

    /**
     * 判断要素的属性是否符合属性条件
     * @param {object} feature    要素对象
     * @param {object} properties 属性条件
     * @returns {boolean} 是否符合
     */
    _isHitProperties(feature, properties) {
        if (properties) {
            for (const [k, v] of Object.entries(properties)) {
                if (feature.properties[k] !== v) {
                    return false;
                }
            }
        }

        return true;
    }

    /**
     * 判断两个几何是否满足下面设定的条件.
     * @param {object} selectGeometry - 要对比的几何
     * @param {object} geometry - 获得的要素几何
     * @returns {Boolean} - 是否满足条件
     */
    _isHitGeometry(selectGeometry, geometry) {
        const selectPixel = this._geojsonConverter.lngLatToPixel(selectGeometry);
        const pixel = this._geojsonConverter.lngLatToPixel(geometry);

        const type = selectGeometry.type;
        switch (type) {
            case 'Point':
            case 'MultiPoint':
                return this.geometryAlgorithm.isWithinDistance(
                    selectPixel,
                    pixel,
                    5
                );
            case 'LineString':
            case 'MultiLineString':
                return this.geometryAlgorithm.intersects(selectPixel, pixel);
            case 'Polygon':
            case 'MultiPolygon':
                return this.geometryAlgorithm.intersectsAndContains(
                    selectPixel,
                    pixel
                );
            case 'GeometryCollection':
                return selectGeometry.geometries.some(item => this._isHitGeometry(selectGeometry, item));
            default:
                throw new Error(`不支持的几何类型:${type}`);
        }
    }

    /**
     * 根据传入的几何，计算与该几何处于包含或者存在交集关系的所有瓦片
     * 首先计算该几何的外包矩形，得到外包矩形的范围的所有瓦片
     * 遍历得到的瓦片和几何对象计算包含和相交关系，最终过滤出所要的瓦片.
     * @param {object}          pixelGeo - 平面像素坐标几何
     * @returns {array<string>} tiles - 瓦片信息对象构成数组
     */
    _getTilesFromContainerGeo(pixelGeo) {
        const bbox = this.geometryAlgorithm.bbox(pixelGeo);
        const tiles = this._getTilesFromBBox(bbox);
        const tileIds = [];
        for (const [name, tile] of Object.entries(tiles)) {
            const tileBounds = this._map.TileManager.getTilePixelBounds(tile);
            const boundsGeo = this._geojsonConverter.wrapBounds(tileBounds);
            if (
                this.geometryAlgorithm.intersectsAndContains(
                    pixelGeo,
                    boundsGeo
                )
            ) {
                tileIds.push(name);
            }
        }

        return tileIds;
    }

    /**
     * 根据传入的范围框返回其范围内所有的瓦片集合
     * @param {object} bbox - 包含该范围最小x,y 最大x,y的对象.
     * @returns {array<object>} tiles.
     */
    _getTilesFromBBox(bbox) {
        const bounds = L.bounds(
            L.point(bbox.minX, bbox.minY),
            L.point(bbox.maxX, bbox.maxY)
        );

        return this._map.TileManager.getTiles(
            this._map.TileManager.tileSize,
            bounds
        );
    }
}
